/*
 * SlavicAccentDoc — stress marks INSIDE a Word (.docx) or OpenDocument (.odt)
 * file, with every style, table, image and page break left as it was.
 *
 * A stress mark is an insertion of one character (U+0301 after the vowel, or '+')
 * into a text node — nothing is reflowed, so unlike translation there is no
 * "which run gets the words" problem: each mark lands in the very run that holds
 * its vowel, and bold/italic/colour inside a word survive.
 *
 * What the analysis needs is CONTEXT: Word splits a paragraph into runs wherever
 * formatting, spell-check state or an editor's revision id changes, often in the
 * middle of a word («вік|на»). So the text of a paragraph is assembled from all
 * its text nodes first, analysed as one string, and only then are the offsets
 * mapped back to the node and the byte where each vowel lives.
 *
 * ⚠️ String-level on purpose (same reasoning as document-translate/ooxml.js): a
 * DOMParser round-trip re-serialises the part and rewrites namespaces, attribute
 * order and empty tags. Here markup outside the text nodes is byte-identical,
 * and inside them only the marks are new.
 *
 * Exposed as window.SlavicAccentDoc.
 */
(function (root) {
    'use strict';
    if (root.SlavicAccentDoc) return;

    var ACUTE = '́';
    var MIME = {
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        odt: 'application/vnd.oasis.opendocument.text'
    };
    var SPEC = {
        // <w:t> holds visible text; <w:delText> (tracked deletion) and <w:instrText>
        // (field codes) are deliberately not touched.
        docx: { parts: /^word\/(document|header\d*|footer\d*|footnotes|endnotes|comments)\.xml$/,
                textTag: 'w:t', para: { 'w:p': 1 },
                seps: { 'w:tab': 1, 'w:br': 1, 'w:cr': 1 } },
        // ODF keeps text as mixed content of <text:p>/<text:h> and <text:span>.
        odt: { parts: /^(content|styles)\.xml$/, textTag: null, para: { 'text:p': 1, 'text:h': 1 },
               seps: { 'text:s': 1, 'text:tab': 1, 'text:line-break': 1, 'text:note': 1, 'text:note-citation': 1 } }
    };

    function kindOf(name) {
        var m = /\.([a-z0-9]+)$/i.exec(name || '');
        var ext = m ? m[1].toLowerCase() : '';
        if (ext === 'docx' || ext === 'odt') return ext;
        return null;
    }

    function entity(e) {
        switch (e) {
            case '&amp;': return '&'; case '&lt;': return '<'; case '&gt;': return '>';
            case '&quot;': return '"'; case '&apos;': return "'";
        }
        var m = /^&#(x[0-9a-f]+|\d+);$/i.exec(e);
        if (!m) return null;
        var cp = m[1][0] === 'x' || m[1][0] === 'X' ? parseInt(m[1].slice(1), 16) : parseInt(m[1], 10);
        try { return String.fromCodePoint(cp); } catch (_) { return null; }
    }

    // Decoded text of a node + for every decoded UTF-16 unit, its index in the raw node.
    function decode(raw) {
        var dec = '', pos = [];
        for (var i = 0; i < raw.length;) {
            if (raw[i] === '&') {
                var semi = raw.indexOf(';', i);
                if (semi > i && semi - i <= 10) {
                    var ch = entity(raw.slice(i, semi + 1));
                    if (ch !== null) {
                        for (var k = 0; k < ch.length; k++) pos.push(i);
                        dec += ch; i = semi + 1; continue;
                    }
                }
            }
            pos.push(i); dec += raw[i]; i++;
        }
        return { dec: dec, pos: pos };
    }

    /**
     * scan(xml, kind) → groups: [{ text, nodes: [{ xs, off, pos, len }] }]
     *   xs  — index in `xml` where the node's raw text starts
     *   off — where its decoded text starts inside group.text
     */
    function scan(xml, kind) {
        var spec = SPEC[kind], groups = [], cur = null, sep = false, inText = false, paraDepth = 0;
        var re = /<[^>]*>|[^<]+/g, m;
        function flush() { if (cur && cur.nodes.length) groups.push(cur); cur = null; sep = false; }
        while ((m = re.exec(xml))) {
            var s = m[0];
            if (s.charCodeAt(0) === 60) {                       // '<'
                var nm = /^<\/?([^\s>\/!?]+)/.exec(s);
                if (!nm) continue;
                var name = nm[1], close = s.charCodeAt(1) === 47, self = s.charCodeAt(s.length - 2) === 47;
                if (spec.para[name]) {
                    flush();
                    if (!self) paraDepth += close ? -1 : 1;
                    if (paraDepth < 0) paraDepth = 0;
                    continue;
                }
                if (spec.textTag && name === spec.textTag) { inText = !close && !self; continue; }
                if (spec.seps[name] && !close) sep = true;
                continue;
            }
            var take = spec.textTag ? inText : paraDepth > 0;
            if (!take) continue;
            var d = decode(s);
            if (!cur) cur = { text: '', nodes: [] };
            if (sep && cur.text) cur.text += ' ';
            sep = false;
            cur.nodes.push({ xs: m.index, off: cur.text.length, pos: d.pos, len: d.dec.length });
            cur.text += d.dec;
        }
        flush();
        return groups;
    }

    // Offsets (into group.text) → absolute indices in xml of the vowel characters.
    function locate(xml, group, offsets, out) {
        var ni = 0, nodes = group.nodes;
        for (var i = 0; i < offsets.length; i++) {
            var o = offsets[i];
            while (ni < nodes.length && o >= nodes[ni].off + nodes[ni].len) ni++;
            if (ni >= nodes.length) break;
            var n = nodes[ni];
            if (o < n.off) continue;                            // fell on a synthetic separator
            var at = n.xs + n.pos[o - n.off];
            if (xml.charCodeAt(at) === 38) continue;            // vowel written as an entity — leave it
            out.push(at);
        }
    }

    function apply(xml, at, fmt) {
        if (!at.length) return xml;
        at.sort(function (a, b) { return a - b; });
        var out = '', last = 0;
        for (var i = 0; i < at.length; i++) {
            var p = at[i];
            if (p < last) continue;
            var ch = xml[p];
            if (fmt === 'plus') out += xml.slice(last, p) + '+' + ch;
            else if (fmt === 'plusafter') out += xml.slice(last, p) + ch + '+';
            else if (fmt === 'caps') out += xml.slice(last, p) + ch.toUpperCase();
            else out += xml.slice(last, p) + ch + ACUTE;
            last = p + 1;
        }
        return out + xml.slice(last);
    }

    /**
     * read(file, JSZip) → { kind, zip, parts:[{name, xml, groups}], texts:[...] }
     * Everything the page needs to detect the language and ask for offsets.
     */
    async function read(file, JSZip) {
        var kind = kindOf(file.name);
        if (!kind) throw Object.assign(new Error('format'), { code: 'format' });
        var zip;
        try { zip = await JSZip.loadAsync(file); }
        catch (e) { throw Object.assign(new Error('zip'), { code: 'corrupt' }); }
        var names = Object.keys(zip.files).filter(function (n) { return SPEC[kind].parts.test(n) && !zip.files[n].dir; });
        if (!names.length) throw Object.assign(new Error('no_parts'), { code: 'corrupt' });
        var parts = [], texts = [];
        for (var i = 0; i < names.length; i++) {
            var xml = await zip.file(names[i]).async('string');
            var groups = scan(xml, kind);
            groups.forEach(function (g) { texts.push(g.text); });
            parts.push({ name: names[i], xml: xml, groups: groups });
        }
        return { kind: kind, zip: zip, parts: parts, texts: texts };
    }

    /** write(doc, offsets, fmt) → Blob of the same format with the marks inside. */
    async function write(doc, offsets, fmt) {
        var k = 0;
        doc.parts.forEach(function (p) {
            var at = [];
            p.groups.forEach(function (g) { locate(p.xml, g, offsets[k++] || [], at); });
            if (at.length) doc.zip.file(p.name, apply(p.xml, at, fmt));
        });
        if (doc.kind === 'odt' && doc.zip.file('mimetype')) {
            // ODF requires `mimetype` first and stored: it keeps its place in the
            // entry order, the option keeps it uncompressed.
            doc.zip.file('mimetype', MIME.odt, { compression: 'STORE' });
        }
        return doc.zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 },
                                       mimeType: MIME[doc.kind] });
    }

    root.SlavicAccentDoc = { kindOf: kindOf, scan: scan, locate: locate, apply: apply, read: read, write: write,
                             decode: decode, MIME: MIME };
    if (typeof module === 'object' && module.exports) module.exports = root.SlavicAccentDoc;
})(typeof self !== 'undefined' ? self : this);
